import { useCallback, useEffect, useState } from 'react';

type useInfiniteScrollProps<T> = {
    scrollElement: HTMLElement | null;
    shouldPopulate?: boolean;
    data: {
        results: T[];
        count: number;
        isFetching: boolean;
    };
    pageState: [number, React.Dispatch<React.SetStateAction<number>>];
};

const useInfiniteScroll = <T>({
    scrollElement,
    data: { results, count, isFetching },
    shouldPopulate = true,
    pageState,
}: useInfiniteScrollProps<T>) => {
    const [page, setPage] = pageState;
    const [items, setItems] = useState<T[]>([]);

    const onLoadMore = useCallback(() => setPage(page + 1), [page]);

    const handleScroll = useCallback(() => {
        if (scrollElement) {
            const { scrollTop, scrollHeight, clientHeight } = scrollElement;
            const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10;
            if (scrolledToBottom && !isFetching && items.length < count) {
                setPage(page + 1);
            }
        }
    }, [scrollElement, count, isFetching, items.length, page]);

    useEffect(() => {
        if (shouldPopulate) {
            setItems((prev) => (page === 1 ? results : [...prev, ...results]));
        }
    }, [results, shouldPopulate]);

    useEffect(() => {
        if (scrollElement) {
            scrollElement.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (scrollElement) {
                scrollElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [scrollElement, handleScroll]);

    return { items, onLoadMore };
};

export { useInfiniteScroll };
